// SCR-STAFF-106 — صف درخواست‌های مراجعه‌کنندگان (AP-PAT-03؛ US-PAT-071، 082، 083 — C-AGENT-1350 تا 1352، 1358): درخواست نسخه داده،
// اصلاح اطلاعات و اتصال پرتال از پرتال یا پذیرش با مهلت ۱۰ روز کاری (BR-PAT-091) و نشان دیرکرد. تأیید نسخه داده با pat.patient.export
// (تولید PDF و فایل ساختاریافته، لینک زمان‌دار، دانلود برای تحویل حضوری و تاریخچه تحویل)، اعمال اصلاح با pat.patient.update_identity
// (مقدار فعلی و پیشنهادی، انتخاب فیلد)، تأیید اتصال پرتال با pat.patient.update پس از بررسی مدرک هویتی و رد با دلیل.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type DataRequestStatus,
  type DataRequestType,
  type DataRequestView,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  ListPage,
  ReasonDialog,
  StatusChip,
  toJalali,
  toJalaliDateTime,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { patientPath, patKeys, saveBlob } from './common';
import { PatientPicker } from './PatientPicker';

export const SCR_STAFF_106 = 'SCR-STAFF-106';

const TYPES: DataRequestType[] = ['Export', 'Correction', 'PortalLink'];
const STATUSES: DataRequestStatus[] = ['Pending', 'Approved', 'Ready', 'Delivered', 'Completed', 'Rejected', 'Failed'];
/** فیلدهای قابل اصلاح (هم‌ارز DataRequest.CorrectableFields سرور). */
export const CORRECTABLE_FIELDS = ['first_name', 'last_name', 'father_name', 'birth_date', 'gender', 'national_id', 'email', 'address'] as const;
const TONES: Record<DataRequestStatus, StatusTone> = {
  Pending: 'pending',
  Approved: 'confirmed',
  Ready: 'booked',
  Delivered: 'done',
  Completed: 'done',
  Rejected: 'cancelled',
  Failed: 'error',
};

function canDecide(access: ReturnType<typeof useAccess>, type: DataRequestType): boolean {
  return access.can(type === 'Export' ? 'pat.patient.export' : type === 'Correction' ? 'pat.patient.update_identity' : 'pat.patient.update');
}

function CorrectionTable({
  request,
  selected,
  onToggle,
  editable,
}: {
  request: DataRequestView;
  selected: string[];
  onToggle: (field: string) => void;
  editable: boolean;
}) {
  const { t } = useTranslation('pat');
  return (
    <Table size="small" aria-label={t('pat:requests.types.Correction')} data-testid="correction-items">
      <TableHead>
        <TableRow>
          {editable && <TableCell padding="checkbox" />}
          <TableCell>{t('pat:requests.field')}</TableCell>
          <TableCell>{t('pat:requests.current')}</TableCell>
          <TableCell>{t('pat:requests.proposed')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {request.items.map((item) => (
          <TableRow key={item.field} data-testid={`correction-${item.field}`}>
            {editable && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.includes(item.field)}
                  onChange={() => onToggle(item.field)}
                  slotProps={{ input: { 'aria-label': t(`pat:requests.fields.${item.field}`) } }}
                />
              </TableCell>
            )}
            <TableCell>{t(`pat:requests.fields.${item.field}`)}</TableCell>
            <TableCell>{item.current ? faDigits(item.current) : t('common:states.none')}</TableCell>
            <TableCell>{item.proposed ? faDigits(item.proposed) : t('common:states.none')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RequestDialog({ request, onClose }: { request: DataRequestView; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [selected, setSelected] = useState<string[]>(request.items.map((item) => item.field));
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const decide = canDecide(access, request.type);
  const open = request.status === 'Pending' || request.status === 'Failed';
  const refresh = () => void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'data-requests'] });
  const path = { params: { path: { requestId: request.id } } };
  const approve = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/patients/data-requests/{requestId}:approve', path)),
    onSuccess: refresh,
  });
  const apply = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/data-requests/{requestId}:apply', {
          ...path,
          body: { reason: null, patientId: null, fields: selected },
        }),
      ),
    onSuccess: () => {
      refresh();
      if (request.patientId) {
        void queryClient.invalidateQueries({ queryKey: patKeys.patient(session?.tenantId, request.patientId) });
      }
    },
  });
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/data-requests/{requestId}:confirm-link', {
          ...path,
          body: { reason: null, patientId: patient?.id ?? null, fields: null },
        }),
      ),
    onSuccess: refresh,
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/data-requests/{requestId}:reject', {
          ...path,
          body: { reason, patientId: null, fields: null },
        }),
      ),
    onSuccess: () => {
      setRejecting(false);
      refresh();
    },
  });
  const download = useMutation({
    mutationFn: async () => {
      const response = await tenantApi.GET('/api/v1/patients/data-requests/{requestId}/file', { ...path, parseAs: 'blob' });
      if (!response.response.ok || !response.data) {
        await unwrap(Promise.resolve(response));
        return;
      }
      saveBlob(response.data as unknown as Blob, `patient-data-${request.id.slice(-8)}.zip`);
      refresh();
    },
  });
  const current = approve.data ?? apply.data ?? confirm.data ?? reject.data ?? request;
  const error = approve.error ?? apply.error ?? confirm.error ?? reject.error ?? download.error;
  const busy = approve.isPending || apply.isPending || confirm.isPending || reject.isPending;
  const settled = current.status !== request.status;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth data-testid="data-request-dialog">
      <DialogTitle id={titleId}>
        {t('pat:requests.detail_title', { type: t(`pat:requests.types.${request.type}`) })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <StatusChip tone={TONES[current.status]} labelKey={`pat:requests.statuses.${current.status}`} />
            <Chip size="small" variant="outlined" label={t(`pat:requests.sources.${request.source}`)} />
            <Typography variant="body2">{t('pat:requests.due', { date: toJalali(request.dueOn) })}</Typography>
            {request.overdue && <Chip size="small" color="error" label={t('pat:requests.overdue')} />}
          </Stack>
          {request.patientId && (
            <Typography>
              <RouterLink to={patientPath(request.patientId)}>{request.patientDisplayName}</RouterLink>
              {request.fileNumber ? ` (${faDigits(request.fileNumber)})` : ''}
            </Typography>
          )}
          {error ? <ErrorState error={error} compact /> : null}
          {settled && (
            <Alert severity="success" data-testid="data-request-done">
              {t(`pat:requests.done.${current.status}`, { defaultValue: t(`pat:requests.statuses.${current.status}`) })}
            </Alert>
          )}

          {request.type === 'Export' && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('pat:requests.approve_hint')}
              </Typography>
              {current.linkExpiresAt && (
                <Typography variant="body2">{t('pat:requests.link_expires', { date: toJalaliDateTime(current.linkExpiresAt) })}</Typography>
              )}
              {current.status === 'Failed' && <Alert severity="warning">{t('pat:requests.failed_hint')}</Alert>}
              <Typography variant="h3">{t('pat:requests.deliveries')}</Typography>
              {current.deliveries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('pat:requests.no_deliveries')}
                </Typography>
              ) : (
                <List dense data-testid="data-request-deliveries">
                  {current.deliveries.map((delivery) => (
                    <ListItem key={`${delivery.channel}-${delivery.deliveredAt}`} disableGutters>
                      <ListItemText
                        primary={t(`pat:requests.delivery_channels.${delivery.channel}`)}
                        secondary={toJalaliDateTime(delivery.deliveredAt)}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}

          {request.type === 'Correction' && (
            <>
              <CorrectionTable
                request={request}
                selected={selected}
                editable={decide && open && !settled}
                onToggle={(field) => setSelected((list) => (list.includes(field) ? list.filter((item) => item !== field) : [...list, field]))}
              />
              <Typography variant="body2" color="text.secondary">
                {t('pat:requests.apply_hint')}
              </Typography>
            </>
          )}

          {request.type === 'PortalLink' && (
            <>
              {request.requester && (
                <Stack spacing={0.5} data-testid="link-requester">
                  <Typography variant="h3">{t('pat:requests.requester')}</Typography>
                  <Typography variant="body2">
                    {[request.requester.firstName, request.requester.lastName].filter(Boolean).join(' ') || t('common:states.none')}
                  </Typography>
                  {request.requester.identifierType && request.requester.identifierLast4 && (
                    <Typography variant="body2">
                      {t('pat:requests.requester_identifier', {
                        type: t(`pat:requests.identifier_types.${request.requester.identifierType}`, {
                          defaultValue: request.requester.identifierType,
                        }),
                        last4: faDigits(request.requester.identifierLast4),
                      })}
                    </Typography>
                  )}
                  <Typography variant="body2" dir="ltr" sx={{ textAlign: 'start' }}>
                    {faDigits(request.requester.mobileMasked)}
                  </Typography>
                  {request.requester.birthDate && (
                    <Typography variant="body2">{t('pat:requests.requester_birth', { date: toJalali(request.requester.birthDate) })}</Typography>
                  )}
                </Stack>
              )}
              {decide && open && !settled && (
                <>
                  <Alert severity="info">{t('pat:requests.confirm_hint')}</Alert>
                  <PatientPicker labelKey="pat:requests.select_patient" value={patient} onChange={setPatient} testId="link-patient" />
                </>
              )}
            </>
          )}
          {current.decisionNote && current.status !== 'Completed' && (
            <Typography variant="body2">{t('pat:requests.decided', { note: current.decisionNote })}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {decide && open && !settled && (
          <Button color="error" onClick={() => setRejecting(true)} disabled={busy} data-testid="data-request-reject">
            {t('pat:requests.reject')}
          </Button>
        )}
        {request.type === 'Export' && decide && (current.status === 'Ready' || current.status === 'Delivered') && (
          <Button onClick={() => download.mutate()} disabled={download.isPending} data-testid="data-request-download">
            {t('pat:requests.download')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
        {request.type === 'Export' && decide && open && !settled && (
          <Button variant="contained" onClick={() => approve.mutate()} disabled={busy} data-testid="data-request-approve">
            {t('pat:requests.approve')}
          </Button>
        )}
        {request.type === 'Correction' && decide && open && !settled && (
          <Button variant="contained" onClick={() => apply.mutate()} disabled={busy || selected.length === 0} data-testid="data-request-apply">
            {t('pat:requests.apply')}
          </Button>
        )}
        {request.type === 'PortalLink' && decide && open && !settled && (
          <Button variant="contained" onClick={() => confirm.mutate()} disabled={busy || !patient} data-testid="data-request-confirm">
            {t('pat:requests.confirm_link')}
          </Button>
        )}
      </DialogActions>
      <ReasonDialog
        open={rejecting}
        titleKey="pat:requests.reject"
        messageKey="pat:requests.reject_message"
        busy={reject.isPending}
        error={reject.error}
        onConfirm={(reason) => reject.mutate(reason)}
        onCancel={() => setRejecting(false)}
      />
    </Dialog>
  );
}

interface NewItem {
  field: (typeof CORRECTABLE_FIELDS)[number];
  proposed: string;
}

function NewRequestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [type, setType] = useState<'Export' | 'Correction'>('Export');
  const [items, setItems] = useState<NewItem[]>([{ field: 'first_name', proposed: '' }]);
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/data-requests', {
          params: { path: { id: patient?.id ?? '' } },
          headers: { 'Idempotency-Key': key.current() },
          body: {
            type,
            items: type === 'Correction' ? items.filter((item) => item.proposed.trim()).map((item) => ({ field: item.field, proposed: item.proposed.trim() })) : null,
            note: null,
          },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'data-requests'] });
      setPatient(null);
      setItems([{ field: 'first_name', proposed: '' }]);
      onClose();
    },
  });
  const invalid = !patient || (type === 'Correction' && !items.some((item) => item.proposed.trim()));
  return (
    <FormDialog
      open={open}
      titleKey="pat:requests.new"
      screenId={SCR_STAFF_106}
      onSubmit={() => !invalid && create.mutate()}
      onClose={onClose}
      isDirty={Boolean(patient)}
      isSubmitting={create.isPending}
      submitError={create.error}
      submitKey="pat:requests.create"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <PatientPicker labelKey="pat:requests.new_patient" value={patient} onChange={setPatient} testId="request-patient" />
        <TextField select label={t('pat:requests.type_label')} value={type} onChange={(event) => setType(event.target.value as 'Export' | 'Correction')}>
          {(['Export', 'Correction'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`pat:requests.types.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        {type === 'Correction' && (
          <Stack spacing={1}>
            {items.map((item, index) => (
              <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  select
                  label={t('pat:requests.field')}
                  value={item.field}
                  onChange={(event) =>
                    setItems((list) => list.map((row, i) => (i === index ? { ...row, field: event.target.value as NewItem['field'] } : row)))
                  }
                  sx={{ minWidth: 180 }}
                >
                  {CORRECTABLE_FIELDS.map((field) => (
                    <MenuItem key={field} value={field}>
                      {t(`pat:requests.fields.${field}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t('pat:requests.proposed')}
                  value={item.proposed}
                  onChange={(event) => setItems((list) => list.map((row, i) => (i === index ? { ...row, proposed: event.target.value } : row)))}
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                />
                {items.length > 1 && (
                  <IconButton aria-label={t('pat:requests.remove_item')} onClick={() => setItems((list) => list.filter((_, i) => i !== index))}>
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Stack>
            ))}
            {items.length < CORRECTABLE_FIELDS.length && (
              <div>
                <Button onClick={() => setItems((list) => [...list, { field: 'last_name', proposed: '' }])}>{t('pat:requests.add_item')}</Button>
              </div>
            )}
          </Stack>
        )}
      </Stack>
    </FormDialog>
  );
}

export function DataRequestsPage() {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [type, setType] = useState<DataRequestType | ''>('');
  const [status, setStatus] = useState<DataRequestStatus | ''>('');
  const [selected, setSelected] = useState<DataRequestView | null>(null);
  const [creating, setCreating] = useState(false);
  const requests = useQuery({
    queryKey: [...patKeys.all(session?.tenantId), 'data-requests', type, status],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/patients/data-requests', {
          params: { query: { type: type || undefined, status: status || undefined, limit: 100 } },
        }),
      ),
  });
  const columns: DataTableColumn<DataRequestView>[] = [
    {
      id: 'patient',
      headerKey: 'pat:requests.columns.patient',
      cell: (row) =>
        row.patientId ? (
          <RouterLink to={patientPath(row.patientId)}>
            {row.patientDisplayName}
            {row.fileNumber ? ` (${faDigits(row.fileNumber)})` : ''}
          </RouterLink>
        ) : (
          <span>{t('pat:requests.unknown_patient')}</span>
        ),
    },
    { id: 'type', headerKey: 'pat:requests.columns.type', cell: (row) => t(`pat:requests.types.${row.type}`) },
    { id: 'source', headerKey: 'pat:requests.columns.source', cell: (row) => t(`pat:requests.sources.${row.source}`) },
    { id: 'requested', headerKey: 'pat:requests.columns.requested', cell: (row) => toJalaliDateTime(row.requestedAt) },
    {
      id: 'due',
      headerKey: 'pat:requests.columns.due',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{toJalali(row.dueOn)}</span>
          {row.overdue && <Chip size="small" color="error" label={t('pat:requests.overdue')} data-testid="request-overdue" />}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'pat:requests.columns.status',
      cell: (row) => <StatusChip tone={TONES[row.status]} labelKey={`pat:requests.statuses.${row.status}`} />,
    },
    {
      id: 'actions',
      headerKey: 'pat:requests.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setSelected(row)} data-testid="request-open">
          {t('pat:requests.open')}
        </Button>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_106}
      titleKey="pat:requests.title"
      subtitleKey="pat:requests.subtitle"
      actions={
        access.can('pat.patient.update') ? (
          <Button variant="outlined" onClick={() => setCreating(true)} data-testid="request-new">
            {t('pat:requests.new')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label={t('pat:requests.type_label')}
            value={type}
            onChange={(event) => setType(event.target.value as DataRequestType | '')}
            sx={{ minWidth: 200 }}
            data-testid="requests-type"
          >
            <MenuItem value="">{t('pat:requests.all')}</MenuItem>
            {TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`pat:requests.types.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('pat:requests.status_label')}
            value={status}
            onChange={(event) => setStatus(event.target.value as DataRequestStatus | '')}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('pat:requests.open_statuses')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`pat:requests.statuses.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
    >
      <DataTable
        captionKey="pat:requests.title"
        columns={columns}
        rows={requests.data?.items}
        isLoading={requests.isPending}
        error={requests.error}
        onRetry={() => void requests.refetch()}
        getRowId={(row) => row.id}
        emptyTitleKey="pat:requests.empty"
      />
      {selected && <RequestDialog key={selected.id} request={selected} onClose={() => setSelected(null)} />}
      <NewRequestDialog open={creating} onClose={() => setCreating(false)} />
    </ListPage>
  );
}
